import * as yup from 'yup';

import { AdditionalSignerRole, ContractDocumentProviders, FinanceType } from 'types';

/**
 * Regex pattern for validating DocuSign template UUIDs.
 * Format: 8-4-4-4-12 alphanumeric characters separated by hyphens.
 */
export const DOCUSIGN_TEMPLATE_ID_REGEX =
  /[A-Za-z0-9]{8}-[A-Za-z0-9]{4}-[A-Za-z0-9]{4}-[A-Za-z0-9]{4}-[A-Za-z0-9]{12}/;

export type ContractProviderValue = ContractDocumentProviders.docusign | ContractDocumentProviders.conga;
export type VaultChoice = '' | 'yes' | 'no';

const VAULT_YES: VaultChoice = 'yes';
const VAULT_NO: VaultChoice = 'no';

const isConga = (provider: unknown) => provider === ContractDocumentProviders.conga;

const templateIdSchema = yup.string().matches(DOCUSIGN_TEMPLATE_ID_REGEX, {
  message: 'Must be a DocuSign UUID',
  excludeEmptyString: true,
});

/**
 * Yup validation schema for contract template forms (add/edit).
 */
export const contractTemplateSchema = yup.object().shape({
  programType: yup.string().required('Program type is required'),
  state: yup.string().required('State is required'),
  // Clearing the input leaves `''`, which yup coerces to NaN. Map only that back to "absent", so an
  // empty optional field passes while real garbage still fails the type check.
  lseId: yup
    .number()
    .transform((value, originalValue) => (originalValue === '' || originalValue == null ? undefined : value))
    .integer('LSE ID must be a whole number')
    .min(1, 'LSE ID must be greater than zero')
    .max(99999, 'LSE ID must be at most 5 digits')
    .notRequired(),
  contractType: yup.string().oneOf(Object.values(FinanceType)),
  provider: yup
    .string()
    .oneOf([ContractDocumentProviders.docusign, ContractDocumentProviders.conga] as ContractProviderValue[])
    .required('Provider is required'),
  requiredAdditionalSigners: yup.array().of(yup.string().oneOf(Object.values(AdditionalSignerRole)).required()),
  congaSpanishAvailable: yup.boolean(),
  vaultOnCompletion: yup
    .string()
    .oneOf(['', VAULT_YES, VAULT_NO] as VaultChoice[])
    .when('provider', {
      is: isConga,
      then: (schema) => schema.required('Choose whether to vault completed contracts'),
    }),
  eOriginalLender: yup.string().when(['provider', 'vaultOnCompletion'], {
    is: (provider: unknown, vault: unknown) => isConga(provider) && vault === VAULT_YES,
    then: (schema) => schema.trim().required('eOriginal lender is required when vaulting'),
  }),
  // Conga rows keep their DocuSign ids so switching back is instant, but nothing requires them.
  englishTemplateId: templateIdSchema.when('provider', {
    is: isConga,
    otherwise: (schema) => schema.required('English template ID is required'),
  }),
  englishCosignTemplateId: templateIdSchema.when('provider', {
    is: isConga,
    otherwise: (schema) => schema.required('English co-borrower template ID is required'),
  }),
  spanishTemplateId: templateIdSchema,
  spanishCosignTemplateId: templateIdSchema,
});

/** The request body the contract template endpoints accept. */
export interface ContractTemplatePayload {
  programType: string;
  state: string;
  lseId?: number;
  contractType?: string;
  provider: ContractProviderValue;
  requiredAdditionalSigners: AdditionalSignerRole[];
  congaSpanishAvailable?: boolean;
  vaultOnCompletion?: boolean;
  eOriginalLender?: string;
  englishTemplateId?: string;
  englishCosignTemplateId?: string;
  spanishTemplateId?: string;
  spanishCosignTemplateId?: string;
}

/** Wider than the payload: Formik stores a cleared number input as `''` and the vault choice as a
 * string, and both start there so the inputs are controlled from the first render. */
export interface ContractTemplateFormValues {
  programType: string;
  state: string;
  lseId: number | '';
  contractType?: string;
  provider: ContractProviderValue;
  requiredAdditionalSigners: AdditionalSignerRole[];
  congaSpanishAvailable: boolean;
  vaultOnCompletion: VaultChoice;
  eOriginalLender: string;
  englishTemplateId: string;
  englishCosignTemplateId: string;
  spanishTemplateId: string;
  spanishCosignTemplateId: string;
}

const TEMPLATE_ID_KEYS = [
  'englishTemplateId',
  'englishCosignTemplateId',
  'spanishTemplateId',
  'spanishCosignTemplateId',
] as const;

/**
 * Formik hands `onSubmit` the raw values, not the validated/cast ones, so an empty `lseId` has to be
 * dropped here. Sending `''` fails the API's `{ type: 'number' }` body schema with a 400.
 */
export const toContractTemplatePayload = ({
  lseId,
  vaultOnCompletion,
  eOriginalLender,
  congaSpanishAvailable,
  ...rest
}: ContractTemplateFormValues): ContractTemplatePayload => {
  const base = lseId === '' ? rest : { ...rest, lseId: Number(lseId) };

  if (!isConga(rest.provider)) {
    return base;
  }

  const isVaulting = vaultOnCompletion === VAULT_YES;
  const payload: ContractTemplatePayload = {
    ...base,
    congaSpanishAvailable,
    vaultOnCompletion: isVaulting,
  };
  if (isVaulting) {
    payload.eOriginalLender = eOriginalLender.trim();
  }
  TEMPLATE_ID_KEYS.forEach((key) => {
    if (!rest[key]) {
      delete payload[key];
    }
  });
  return payload;
};
